import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  verticalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import get from "lodash/get";
import PropTypes from "prop-types";

import { TreeModifier } from "@/components/InterventionTemplate/TreeModifier";

const Context = ({
  source,
  sortableContainerKey = "",
  sortableItemId = "",
  activeNode,
  onMoveEnd,
  onMoveOver,
  onChangeActiveNode,
  dragOverlay,
  children,
}) => {
  const options = {
    // Press distance of min 5px to detect drag
    activationConstraint: {
      distance: 5,
    },
  };

  const sensors = useSensors(
    useSensor(PointerSensor, options),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Empty active node and call the callback
   *
   * @return {void}
   */
  function onDragEnd() {
    onChangeActiveNode(null);
    return onMoveEnd();
  }

  /**
   * Move the element and call the callback
   *
   * @param {object} event
   * @return {void}
   */
  function onDragOver(event) {
    // Extract
    const paths = extractMove(event);
    if (!paths) return;
    const { initialPath, destinationPath } = paths;

    // Transform
    const tempSource = TreeModifier.modify(
      source,
      activeNode,
      initialPath,
      destinationPath
    );
    if (tempSource == source) return;

    onMoveOver(tempSource);
    onChangeActiveNode(activeNode);

    return;
  }

  /**
   * Set the active path to display the DragOverlay
   *
   * @param {object} event
   * @return {void}
   */
  function onDragStart(event) {
    const { active } = event;
    const activePath = active?.data?.current?.path;
    const node = active?.data?.current?.node;
    onChangeActiveNode(get(source, activePath, node));
  }

  /**
   * Move the element
   *
   * @param {object} event
   * @return {object} modified source
   */
  function extractMove(event) {
    // Extract initial path and destination
    const { active, over } = event;
    if (!active || !over) return null;

    const initialPath = active?.data?.current?.path;
    const destinationPath = over?.data?.current?.path;

    if (initialPath == destinationPath) return null; // No move

    return { initialPath, destinationPath };
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
    >
      <SortableContext
        items={TreeModifier.getSortablePaths(source, sortableContainerKey).map(
          (path) => get(source, path)[sortableItemId]
        )}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeNode && dragOverlay ? dragOverlay(activeNode) : null}
      </DragOverlay>
    </DndContext>
  );
};

Context.propTypes = {
  source: PropTypes.object.isRequired,
  sortableContainerKey: PropTypes.string,
  onMoveEnd: PropTypes.func.isRequired,
  onMoveOver: PropTypes.func.isRequired,
  onChangeActiveNode: PropTypes.func.isRequired,
  dragOverlay: PropTypes.func,
  children: PropTypes.node,
  sortableItemId: PropTypes.string,
  activeNode: PropTypes.node,
};

export default Context;
